import type { PhrasingContent, Root } from "mdast"
import rehypeSanitize from "rehype-sanitize"
import remarkBreaks from "remark-breaks"
import remarkGfm from "remark-gfm"
import { visit } from "unist-util-visit"
import { visitParents } from "unist-util-visit-parents"

export const rehypePlugins = [rehypeSanitize]

export const remarkPlugins = [
  remarkBreaks,
  remarkGfm,
  remarkPreventGithubAutolinks,
  remarkGithubRefs,
]

export const allowedHTMLElements = [
  "a",
  "b",
  "blockquote",
  "br",
  "code",
  "dd",
  "del",
  "details",
  "div",
  "dl",
  "dt",
  "em",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "hr",
  "i",
  "ins",
  "kbd",
  "li",
  "ol",
  "p",
  "pre",
  "q",
  "rp",
  "rt",
  "ruby",
  "s",
  "samp",
  "source",
  "span",
  "strike",
  "strong",
  "sub",
  "summary",
  "sup",
  "table",
  "tbody",
  "td",
  "tfoot",
  "th",
  "thead",
  "tr",
  "ul",
  "var",
] as const

// TODO: Switch to arkregex
// Based on https://github.com/remarkjs/remark-github
const userGroup = "[\\da-z][-\\da-z]{0,38}"
const projectGroup = "(?:\\.git[\\w-]|\\.(?!git)|[\\w-])+"
const repoGroup = `(${userGroup})\\/(${projectGroup})`

const linkRegex = new RegExp(
  "^https?:\\/\\/github\\.com\\/" +
    repoGroup +
    "\\/(issues|pull)\\/([1-9]\\d*)\\/?$",
  "g",
)

const issueRegex = /#([1-9]\d*)/g

function remarkGithubRefs() {
  return (tree: Root) => {
    visitParents(tree, "text", (node, parents) => {
      const parent = parents[parents.length - 1]
      if (!parent) return
      if (parents.some((ancestor) => ancestor.type === "githubReference"))
        return

      const index = parent.children.indexOf(node as never)
      if (index === undefined) return

      const text = node.value
      let matches = Array.from(text.matchAll(issueRegex))
      if (!matches.length) {
        linkRegex.lastIndex = 0
        matches = Array.from(text.matchAll(linkRegex))
      }
      if (matches.length === 0) return

      const nodes: PhrasingContent[] = []
      let lastIndex = 0

      for (const match of matches) {
        if (match.index! > lastIndex)
          nodes.push({
            type: "text",
            value: text.slice(lastIndex, match.index),
          })

        const type = match.length === 5 ? "link" : "direct"
        const number = Number.parseInt(
          type === "link" ? match[4]! : match[1]!,
          10,
        )

        nodes.push({
          type: "githubReference",
          children: [
            {
              type: "text",
              value: match[0],
            },
          ],
          data: {
            hName: "span",
            hProperties: {
              "data-expanded": parents.some(
                (ancestor) => ancestor.type === "listItem",
              ),
              "data-github-owner": type === "link" ? match[1] : undefined,
              "data-github-reference": number,
              "data-github-repo": type === "link" ? match[2] : undefined,
              "data-github-type":
                type === "link" ? (match[3] as never) : "unknown",
            },
          },
          number,
        })

        lastIndex = match.index! + match[0].length
      }

      if (lastIndex < text.length)
        nodes.push({
          type: "text",
          value: text.slice(lastIndex),
        })

      parent.children.splice(index, 1, ...nodes)
    })
  }
}

declare module "mdast" {
  interface RootContentMap {
    githubReference: GitHubReferenceNode
  }
  interface PhrasingContentMap {
    githubReference: GitHubReferenceNode
  }
}

interface GitHubReferenceNode {
  type: "githubReference"
  children: Array<{ type: "text"; value: string }>
  data?: {
    hName?: string | undefined
    hProperties?:
      | {
          "data-expanded": boolean
          "data-github-owner": string | undefined
          "data-github-type": "issues" | "pull" | "unknown"
          "data-github-reference": number
          "data-github-repo": string | undefined
        }
      | undefined
  }
  number: number
}

function remarkPreventGithubAutolinks() {
  return (tree: Root) => {
    visit(tree, "link", (node, index, parent) => {
      if (!parent || index === undefined) return

      linkRegex.lastIndex = 0
      if (!linkRegex.test(node.url)) return

      const isAutolink =
        node.children.length === 1 &&
        node.children[0]?.type === "text" &&
        node.children[0].value === node.url

      if (!isAutolink) return

      parent.children.splice(index, 1, {
        type: "text" as const,
        value: node.url,
      })
    })
  }
}

if (import.meta.vitest) {
  const { expect, test } = import.meta.vitest
  const { remark } = await import("remark")

  test.each([
    {
      name: "issue reference #123 converted to githubReference node",
      markdown: "See issue #123 for details",
      expected: {
        type: "paragraph",
        children: [
          {
            type: "text",
            value: "See issue ",
          },
          {
            type: "githubReference",
            number: 123,
            children: [
              {
                type: "text",
                value: "#123",
              },
            ],
            data: {
              hName: "span",
              hProperties: {
                "data-expanded": false,
                "data-github-owner": undefined,
                "data-github-reference": 123,
                "data-github-repo": undefined,
                "data-github-type": "unknown",
              },
            },
          },
          {
            type: "text",
            value: " for details",
          },
        ],
      },
    },
    {
      name: "multiple issue references converted",
      markdown: "Issues #123 and #456 are related",
      expected: {
        type: "paragraph",
        children: [
          {
            type: "text",
            value: "Issues ",
          },
          {
            type: "githubReference",
            number: 123,
            children: [
              {
                type: "text",
                value: "#123",
              },
            ],
            data: {
              hName: "span",
              hProperties: {
                "data-expanded": false,
                "data-github-owner": undefined,
                "data-github-reference": 123,
                "data-github-repo": undefined,
                "data-github-type": "unknown",
              },
            },
          },
          {
            type: "text",
            value: " and ",
          },
          {
            type: "githubReference",
            number: 456,
            children: [
              {
                type: "text",
                value: "#456",
              },
            ],
            data: {
              hName: "span",
              hProperties: {
                "data-expanded": false,
                "data-github-owner": undefined,
                "data-github-reference": 456,
                "data-github-repo": undefined,
                "data-github-type": "unknown",
              },
            },
          },
          {
            type: "text",
            value: " are related",
          },
        ],
      },
    },
    {
      name: "issue link converted to githubReference node",
      markdown: "Check https://github.com/owner/repo/issues/123",
      expected: {
        type: "paragraph",
        children: [
          {
            type: "text",
            value: "Check ",
          },
          {
            type: "githubReference",
            number: 123,
            children: [
              {
                type: "text",
                value: "https://github.com/owner/repo/issues/123",
              },
            ],
            data: {
              hName: "span",
              hProperties: {
                "data-expanded": false,
                "data-github-owner": "owner",
                "data-github-reference": 123,
                "data-github-repo": "repo",
                "data-github-type": "issues",
              },
            },
          },
        ],
      },
    },
    {
      name: "PR link converted to githubReference node",
      markdown: "See https://github.com/owner/repo/pull/456",
      expected: {
        type: "paragraph",
        children: [
          {
            type: "text",
            value: "See ",
          },
          {
            type: "githubReference",
            number: 456,
            children: [
              {
                type: "text",
                value: "https://github.com/owner/repo/pull/456",
              },
            ],
            data: {
              hName: "span",
              hProperties: {
                "data-expanded": false,
                "data-github-owner": "owner",
                "data-github-reference": 456,
                "data-github-repo": "repo",
                "data-github-type": "pull",
              },
            },
          },
        ],
      },
    },
    {
      name: "data-expanded set to true when inside list item",
      markdown: "- Issue #123 in list",
      expected: {
        type: "list",
        children: [
          {
            type: "listItem",
            children: [
              {
                type: "paragraph",
                children: [
                  {
                    type: "text",
                    value: "Issue ",
                  },
                  {
                    type: "githubReference",
                    number: 123,
                    children: [
                      {
                        type: "text",
                        value: "#123",
                      },
                    ],
                    data: {
                      hName: "span",
                      hProperties: {
                        "data-expanded": true,
                        "data-github-owner": undefined,
                        "data-github-reference": 123,
                        "data-github-repo": undefined,
                        "data-github-type": "unknown",
                      },
                    },
                  },
                  {
                    type: "text",
                    value: " in list",
                  },
                ],
              },
            ],
          },
        ],
      },
    },
    {
      name: "#0 (invalid issue number) not converted",
      markdown: "Issue #0 is invalid",
      expected: {
        type: "paragraph",
        children: [
          {
            type: "text",
            value: "Issue #0 is invalid",
          },
        ],
      },
    },
    {
      name: "hashtag or CSS selector not converted",
      markdown: "Use #hashtag or CSS #selector",
      expected: {
        type: "paragraph",
        children: [
          {
            type: "text",
            value: "Use #hashtag or CSS #selector",
          },
        ],
      },
    },
    {
      name: "mixed GitHub links and issue references",
      markdown: "See #123 and https://github.com/owner/repo/pull/456",
      expected: {
        type: "paragraph",
        children: [
          {
            type: "text",
            value: "See ",
          },
          {
            type: "githubReference",
            number: 123,
            children: [
              {
                type: "text",
                value: "#123",
              },
            ],
            data: {
              hName: "span",
              hProperties: {
                "data-expanded": false,
                "data-github-owner": undefined,
                "data-github-reference": 123,
                "data-github-repo": undefined,
                "data-github-type": "unknown",
              },
            },
          },
          {
            type: "text",
            value: " and ",
          },
          {
            type: "githubReference",
            number: 456,
            children: [
              {
                type: "text",
                value: "https://github.com/owner/repo/pull/456",
              },
            ],
            data: {
              hName: "span",
              hProperties: {
                "data-expanded": false,
                "data-github-owner": "owner",
                "data-github-reference": 456,
                "data-github-repo": "repo",
                "data-github-type": "pull",
              },
            },
          },
        ],
      },
    },
    {
      name: "issue reference at start of text",
      markdown: "#123 is the main issue",
      expected: {
        type: "paragraph",
        children: [
          {
            type: "githubReference",
            number: 123,
            children: [
              {
                type: "text",
                value: "#123",
              },
            ],
            data: {
              hName: "span",
              hProperties: {
                "data-expanded": false,
                "data-github-owner": undefined,
                "data-github-reference": 123,
                "data-github-repo": undefined,
                "data-github-type": "unknown",
              },
            },
          },
          {
            type: "text",
            value: " is the main issue",
          },
        ],
      },
    },
    {
      name: "issue reference at end of text",
      markdown: "Main issue is #123",
      expected: {
        type: "paragraph",
        children: [
          {
            type: "text",
            value: "Main issue is ",
          },
          {
            type: "githubReference",
            number: 123,
            children: [
              {
                type: "text",
                value: "#123",
              },
            ],
            data: {
              hName: "span",
              hProperties: {
                "data-expanded": false,
                "data-github-owner": undefined,
                "data-github-reference": 123,
                "data-github-repo": undefined,
                "data-github-type": "unknown",
              },
            },
          },
        ],
      },
    },
    {
      name: "pull request comment not converted",
      markdown:
        "https://github.com/wevm/gitchat/pull/46#discussion_r2204190481",
      expected: {
        type: "paragraph",
        children: [
          {
            type: "link",
            url: "https://github.com/wevm/gitchat/pull/46#discussion_r2204190481",
            children: [
              {
                type: "text",
                value:
                  "https://github.com/wevm/gitchat/pull/46#discussion_r2204190481",
              },
            ],
          },
        ],
      },
    },
    {
      name: "issue comment not converted",
      markdown:
        "https://github.com/wevm/wagmi/issues/4478#issuecomment-2563769998",
      expected: {
        type: "paragraph",
        children: [
          {
            type: "link",
            url: "https://github.com/wevm/wagmi/issues/4478#issuecomment-2563769998",
            children: [
              {
                type: "text",
                value:
                  "https://github.com/wevm/wagmi/issues/4478#issuecomment-2563769998",
              },
            ],
          },
        ],
      },
    },
  ] satisfies readonly {
    name: string
    markdown: string
    expected: {
      type: string
      children?: Root["children"] | undefined
    }
  }[])("remarkGithubRefs: $name", ({ markdown, expected }) => {
    const processor = remark().use([
      remarkGfm,
      remarkPreventGithubAutolinks,
      remarkGithubRefs,
    ])
    const ast = processor.parse(markdown)
    const result = processor.runSync(ast) as Root
    expect(result.children[0]).toMatchObject(expected)
  })

  test.each([
    {
      name: "issue autolink converted to plain text",
      markdown: "https://github.com/owner/repo/issues/123",
      expected: {
        type: "paragraph",
        children: [
          {
            type: "text",
            value: "https://github.com/owner/repo/issues/123",
          },
        ],
      },
    },
    {
      name: "PR autolink converted to plain text",
      markdown: "https://github.com/owner/repo/pull/456",
      expected: {
        type: "paragraph",
        children: [
          {
            type: "text",
            value: "https://github.com/owner/repo/pull/456",
          },
        ],
      },
    },
    {
      name: "issue link with custom text kept as link",
      markdown: "[GitHub Issue](https://github.com/owner/repo/issues/123)",
      expected: {
        type: "paragraph",
        children: [
          {
            type: "link",
            url: "https://github.com/owner/repo/issues/123",
            children: [
              {
                type: "text",
                value: "GitHub Issue",
              },
            ],
          },
        ],
      },
    },
    {
      name: "PR link with custom text kept as link",
      markdown: "[PR Link](https://github.com/owner/repo/pull/456)",
      expected: {
        type: "paragraph",
        children: [
          {
            type: "link",
            url: "https://github.com/owner/repo/pull/456",
            children: [
              {
                type: "text",
                value: "PR Link",
              },
            ],
          },
        ],
      },
    },
    {
      name: "non-GitHub autolink kept as link",
      markdown: "https://github.com/owner/repo/issues/abc123...def456",
      expected: {
        type: "paragraph",
        children: [
          {
            type: "link",
            url: "https://github.com/owner/repo/issues/abc123...def456",
            children: [
              {
                type: "text",
                value: "https://github.com/owner/repo/issues/abc123...def456",
              },
            ],
          },
        ],
      },
    },
    {
      name: "non-GitHub autolink kept as link",
      markdown: "https://example.com/issues/123",
      expected: {
        type: "paragraph",
        children: [
          {
            type: "link",
            url: "https://example.com/issues/123",
            children: [
              {
                type: "text",
                value: "https://example.com/issues/123",
              },
            ],
          },
        ],
      },
    },
    {
      name: "repo URL (non-issue/PR) kept as link",
      markdown: "https://github.com/owner/repo",
      expected: {
        type: "paragraph",
        children: [
          {
            type: "link",
            url: "https://github.com/owner/repo",
            children: [
              {
                type: "text",
                value: "https://github.com/owner/repo",
              },
            ],
          },
        ],
      },
    },
    {
      name: "mixed content with autolink and custom link",
      markdown:
        "Check out https://github.com/owner/repo/issues/123 and [this PR](https://github.com/owner/repo/pull/456)",
      expected: {
        type: "paragraph",
        children: [
          {
            type: "text",
            value: "Check out ",
          },
          {
            type: "text",
            value: "https://github.com/owner/repo/issues/123",
          },
          {
            type: "text",
            value: " and ",
          },
          {
            type: "link",
            url: "https://github.com/owner/repo/pull/456",
            children: [
              {
                type: "text",
                value: "this PR",
              },
            ],
          },
        ],
      },
    },
    {
      name: "pull request comment kept as link",
      markdown:
        "https://github.com/wevm/gitchat/pull/46#discussion_r2204190481",
      expected: {
        type: "paragraph",
        children: [
          {
            type: "link",
            url: "https://github.com/wevm/gitchat/pull/46#discussion_r2204190481",
            children: [
              {
                type: "text",
                value:
                  "https://github.com/wevm/gitchat/pull/46#discussion_r2204190481",
              },
            ],
          },
        ],
      },
    },
    {
      name: "issue comment kept as link",
      markdown:
        "https://github.com/wevm/wagmi/issues/4478#issuecomment-2563769998",
      expected: {
        type: "paragraph",
        children: [
          {
            type: "link",
            url: "https://github.com/wevm/wagmi/issues/4478#issuecomment-2563769998",
            children: [
              {
                type: "text",
                value:
                  "https://github.com/wevm/wagmi/issues/4478#issuecomment-2563769998",
              },
            ],
          },
        ],
      },
    },
  ] satisfies readonly {
    name: string
    markdown: string
    expected: {
      type: "paragraph"
      children: Array<{
        type: "text" | "link"
        value?: string | undefined
        url?: string | undefined
        children?: Root["children"] | undefined
      }>
    }
  }[])("remarkPreventGithubAutolinks: $name", ({ markdown, expected }) => {
    const processor = remark().use([remarkGfm, remarkPreventGithubAutolinks])
    const ast = processor.parse(markdown)
    const result = processor.runSync(ast) as Root
    expect(result.children[0]).toMatchObject(expected)
  })
}
